import { Alert } from '@creditornot/cb-alert';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { MOBILE_BREAKPOINT, useIsMobile } from 'common/hooks/useIsMobile';
import { typographyCss } from 'common/typography';
import Loading from 'core/Loading';
import { DiscountedItem } from 'modules/campaigns/hooks/useDiscountedItems';

import { SelectedDiscountItem } from './SelectedDiscountItem';
import { SelectedDiscountedItemsModal } from './SelectedDiscountedItemsModal';
import { groupDiscountedItemsByCategory } from './utils';

type DiscountedItemsGridProps = {
  selectedItemIds: string[];
  menuItems: DiscountedItem[];
  isLoadingMenuData: boolean;
  onEditClick?: () => void;
};

const Category = styled.div``;

const SelectedItemsGrid = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  @media (width < ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: 1fr;
  }
`;

const CategoryTitle = styled.h3`
  ${typographyCss.Body3}
  font-weight: 500;
  margin-block: 0 8px;
`;

const MAX_VISIBLE_ITEM_COUNT = 16;
const MAX_VISIBLE_ITEM_COUNT_MOBILE = 8;

export const DiscountedItemsGrid = ({
  selectedItemIds,
  menuItems,
  isLoadingMenuData,
  onEditClick,
}: DiscountedItemsGridProps) => {
  const isMobile = useIsMobile();
  const maxItems = isMobile ? MAX_VISIBLE_ITEM_COUNT_MOBILE : MAX_VISIBLE_ITEM_COUNT;

  const selectedItems = menuItems.filter(item => selectedItemIds.includes(item.id));
  const hasMissingItems = selectedItemIds.length > selectedItems.length;

  const hasMoreItems = selectedItems.length > maxItems;

  const itemsByCategory = useMemo(() => groupDiscountedItemsByCategory(selectedItems), [selectedItems]);

  const fullyVisible = useMemo(
    () =>
      itemsByCategory.reduce(
        ({ itemCount, categoryCount }, category) => {
          const currentItemCount = itemCount + category.items.length;
          if (currentItemCount <= maxItems) {
            return { itemCount: currentItemCount, categoryCount: categoryCount + 1 };
          }
          return { itemCount: currentItemCount, categoryCount };
        },
        { itemCount: 0, categoryCount: 0 },
      ),
    [itemsByCategory, maxItems],
  );

  if (isLoadingMenuData) {
    return <Loading />;
  }

  return (
    <>
      {selectedItems.length === 1 ? (
        <SelectedDiscountItem item={selectedItems[0]} />
      ) : (
        itemsByCategory.map((category, categoryIndex) =>
          // Always show at least one category
          categoryIndex !== 0 && categoryIndex >= fullyVisible.categoryCount ? null : (
            <Category key={`category-${category.id}`}>
              <CategoryTitle>{`${category.name} (${category.items.length})`}</CategoryTitle>
              <SelectedItemsGrid>
                {category.items.map((item, itemIndex) =>
                  // If showing only one category, limit the number of items visible
                  fullyVisible.categoryCount === 0 && itemIndex >= maxItems ? null : (
                    <SelectedDiscountItem key={item.id} item={item} />
                  ),
                )}
              </SelectedItemsGrid>
            </Category>
          ),
        )
      )}
      {hasMissingItems && (
        <Alert variant="error">
          <FormattedMessage id="campaigns.form.item.no-item" />
        </Alert>
      )}
      {hasMoreItems && <SelectedDiscountedItemsModal selectedItems={selectedItems} onEditClick={onEditClick} />}
    </>
  );
};
